/**
 * ErrorBoundary
 *
 * A React class component that catches runtime errors anywhere in its
 * child component tree and renders a graceful fallback UI instead of
 * crashing the entire application.
 *
 * Error classification:
 *   - Network / fetch failures  -> connection-specific guidance
 *   - 404 / routing errors      -> "page not found" message
 *   - Everything else           -> generic fallback with team notification note
 *
 * In development mode (import.meta.env.DEV), a collapsible section
 * exposes the raw error message and stack trace for debugging.
 *
 * Design system: Navy / Blue / Emerald
 * Fonts: Urbanist (headings), Poppins (body)
 * Icons: lucide-react only
 */

import React from "react";
import { AlertTriangle, RotateCcw, LayoutDashboard, ChevronDown, ChevronUp } from "lucide-react";

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

interface ErrorBoundaryProps {
  children: React.ReactNode;
  /** Optional custom fallback UI. When provided the default error card is bypassed. */
  fallback?: React.ReactNode;
}

interface ErrorBoundaryState {
  hasError: boolean;
  error: Error | null;
  errorInfo: React.ErrorInfo | null;
  detailsOpen: boolean;
}

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

type ErrorCategory = "network" | "not-found" | "generic";

function classifyError(error: Error | null): ErrorCategory {
  if (!error) return "generic";

  const message = (error.message ?? "").toLowerCase();
  const name = (error.name ?? "").toLowerCase();

  // Network / fetch related
  if (
    name === "typeerror" && message.includes("fetch") ||
    message.includes("networkerror") ||
    message.includes("network request failed") ||
    message.includes("failed to fetch") ||
    message.includes("net::err") ||
    message.includes("econnrefused") ||
    message.includes("enotfound") ||
    message.includes("load failed")
  ) {
    return "network";
  }

  // 404 / routing
  if (
    message.includes("404") ||
    message.includes("not found") ||
    message.includes("no match") ||
    message.includes("cannot find route")
  ) {
    return "not-found";
  }

  return "generic";
}

const ERROR_MESSAGES: Record<ErrorCategory, string> = {
  network:
    "We lost the connection. Please check your internet and try again.",
  "not-found":
    "This page could not be found.",
  generic:
    "An unexpected error occurred. Our team has been notified.",
};

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export class ErrorBoundary extends React.Component<
  ErrorBoundaryProps,
  ErrorBoundaryState
> {
  constructor(props: ErrorBoundaryProps) {
    super(props);
    this.state = {
      hasError: false,
      error: null,
      errorInfo: null,
      detailsOpen: false,
    };
  }

  // ---- Static lifecycle ----

  static getDerivedStateFromError(error: Error): Partial<ErrorBoundaryState> {
    return { hasError: true, error };
  }

  // ---- Instance lifecycle ----

  componentDidCatch(error: Error, errorInfo: React.ErrorInfo): void {
    this.setState({ errorInfo });

    // In a real app you would report to an error tracking service here.
    // e.g. Sentry.captureException(error, { extra: errorInfo });
    if (import.meta.env.DEV) {
      console.error("[ErrorBoundary] Caught error:", error, errorInfo);
    }
  }

  // ---- Handlers ----

  private handleReset = (): void => {
    this.setState({
      hasError: false,
      error: null,
      errorInfo: null,
      detailsOpen: false,
    });
  };

  private toggleDetails = (): void => {
    this.setState((prev) => ({ detailsOpen: !prev.detailsOpen }));
  };

  // ---- Render ----

  render(): React.ReactNode {
    const { hasError, error, errorInfo, detailsOpen } = this.state;
    const { children, fallback } = this.props;

    if (!hasError) {
      return children;
    }

    // If a custom fallback was supplied, use it directly.
    if (fallback) {
      return fallback;
    }

    const category = classifyError(error);
    const contextMessage = ERROR_MESSAGES[category];
    const isDev = import.meta.env.DEV;

    return (
      <div
        className="flex items-center justify-center min-h-screen bg-background px-4"
        role="alert"
        aria-live="assertive"
      >
        {/* Error Card */}
        <div className="bg-card rounded-2xl shadow-elevated border border-border p-8 max-w-lg w-full animate-scale-in">
          {/* Icon */}
          <div className="flex justify-center mb-6">
            <div
              className="flex items-center justify-center w-16 h-16 rounded-full bg-red-50 dark:bg-red-950/30"
              aria-hidden="true"
            >
              <AlertTriangle className="w-8 h-8 text-red-500" />
            </div>
          </div>

          {/* Heading */}
          <h1 className="text-2xl font-semibold text-center font-heading text-foreground mb-2">
            Something went wrong
          </h1>

          {/* Contextual message */}
          <p className="text-center text-muted-foreground font-body leading-relaxed mb-8">
            {contextMessage}
          </p>

          {/* Actions */}
          <div className="flex flex-col sm:flex-row items-center justify-center gap-3">
            <button
              type="button"
              onClick={this.handleReset}
              className="bg-navy-800 hover:bg-navy-700 text-white px-6 py-2.5 rounded-xl font-medium
                         inline-flex items-center gap-2 transition-colors focus-visible:outline-none
                         focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2
                         font-body text-sm"
              aria-label="Try again"
            >
              <RotateCcw className="w-4 h-4" aria-hidden="true" />
              Try Again
            </button>

            <a
              href="/dashboard"
              className="inline-flex items-center gap-2 px-6 py-2.5 rounded-xl font-medium
                         text-navy-700 dark:text-navy-300 hover:bg-navy-50 dark:hover:bg-navy-900/40
                         transition-colors focus-visible:outline-none focus-visible:ring-2
                         focus-visible:ring-ring focus-visible:ring-offset-2
                         font-body text-sm"
              aria-label="Go to Dashboard"
            >
              <LayoutDashboard className="w-4 h-4" aria-hidden="true" />
              Go to Dashboard
            </a>
          </div>

          {/* Dev-only: collapsible error details */}
          {isDev && error && (
            <div className="mt-8 border-t border-border pt-6">
              <button
                type="button"
                onClick={this.toggleDetails}
                className="flex items-center gap-2 text-xs font-medium text-muted-foreground
                           hover:text-foreground transition-colors font-body
                           focus-visible:outline-none focus-visible:ring-2
                           focus-visible:ring-ring focus-visible:ring-offset-2 rounded-md"
                aria-expanded={detailsOpen}
                aria-controls="error-details-panel"
              >
                {detailsOpen ? (
                  <ChevronUp className="w-3.5 h-3.5" aria-hidden="true" />
                ) : (
                  <ChevronDown className="w-3.5 h-3.5" aria-hidden="true" />
                )}
                Error Details
              </button>

              {detailsOpen && (
                <div
                  id="error-details-panel"
                  role="region"
                  aria-label="Error details"
                  className="mt-3 rounded-xl bg-navy-950/5 dark:bg-navy-950/40 border border-border p-4
                             overflow-auto max-h-64 text-xs font-mono leading-relaxed"
                >
                  {/* Error message */}
                  <p className="text-red-600 dark:text-red-400 font-semibold mb-2 break-words">
                    {error.name}: {error.message}
                  </p>

                  {/* Stack trace */}
                  {(error.stack || errorInfo?.componentStack) && (
                    <pre className="whitespace-pre-wrap text-muted-foreground break-words">
                      {error.stack}
                      {errorInfo?.componentStack && (
                        <>
                          {"\n\nComponent Stack:"}
                          {errorInfo.componentStack}
                        </>
                      )}
                    </pre>
                  )}
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    );
  }
}

export default ErrorBoundary;
